import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Paper, Stack } from '@mui/material';

interface SignaturePadProps {
  onChange: (signature: string) => void;
  height?: number;
  width?: number;
  showButtons?: boolean;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onChange,
  height = 200,
  width = '100%',
  showButtons = false
}) => {
  const signaturePad = useRef<SignatureCanvas>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [padDimensions, setPadDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setPadDimensions({ width, height: height - 20 }); // Subtract padding
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const clear = () => {
    if (signaturePad.current) {
      signaturePad.current.clear();
      onChange('');
    }
  };

  const save = () => {
    if (signaturePad.current) {
      const signatureData = signaturePad.current.toDataURL();
      onChange(signatureData);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        ref={containerRef}
        elevation={0} 
        sx={{ 
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'transparent'
        }}
      >
        <Box 
          sx={{ 
            border: '1px solid rgba(255, 255, 255, 0.23)',
            borderRadius: 1,
            backgroundColor: '#fff',
            width: '100%',
            height: height,
            overflow: 'hidden'
          }}
        >
          {padDimensions.width > 0 && (
            <SignatureCanvas
              ref={signaturePad}
              canvasProps={{
                width: padDimensions.width,
                height: padDimensions.height,
                className: 'signature-canvas',
                style: {
                  width: '100%',
                  height: '100%'
                }
              }}
            />
          )}
        </Box>
        
        {showButtons && (
          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ 
              mt: 1,
              justifyContent: 'space-between'
            }}
          >
            <Button 
              variant="outlined" 
              onClick={clear}
              sx={{
                flex: 1,
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              Clear
            </Button>
            <Button 
              variant="contained" 
              onClick={save}
              sx={{
                flex: 1,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              Save Signature
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}; 